import { supabase } from './supabase';
import { User, Session } from '@supabase/supabase-js';
import {
  validateDisplayName,
  validateBio,
  validateAvatarUrl
} from '../utils/validation';

export interface AuthUser {
  id: string;
  email?: string;
  displayName?: string;
  avatarUrl?: string;
  role?: 'admin' | 'moderator' | 'user';
}

class AuthService {
  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle() {
    // Guardar el dominio actual antes de redirigir
    localStorage.setItem('auth_origin_domain', window.location.origin);

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) throw error;
    return data;
  }

  /**
   * Sign out
   */
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  /**
   * Get current session
   */
  async getSession(): Promise<Session | null> {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User | null> {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return data.user;
  }

  /**
   * Get user profile from profiles table
   */
  async getUserProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, display_name, avatar_url, banner_url, bio, role, created_at')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Update user profile
   */
  async updateProfile(userId: string, updates: {
    display_name?: string;
    bio?: string;
    avatar_url?: string;
  }) {
    // Validate inputs
    if (updates.display_name) {
      validateDisplayName(updates.display_name);
    }
    if (updates.bio !== undefined) {
      validateBio(updates.bio);
    }
    if (updates.avatar_url) {
      validateAvatarUrl(updates.avatar_url);
    }

    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Check if username is available
   * Note: Username field removed from database
   */
  async isUsernameAvailable(username: string): Promise<boolean> {
    // Username functionality disabled - always return true
    return true;
  }

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });
  }
}

export const authService = new AuthService();
